import { currentProfileAtom } from '@/atoms/profile'
import { pomegranateAuthAtom } from '@/atoms/pomegranate'
import { savePendingEvent } from '@/atoms/pending'
import { clearNsecRecord, recordNsecGenerated } from '@/atoms/key-backup'
import { createRelayListDraftEvent } from '@/lib/draft-event'
import {
  addUsernameToIndex,
  determineTargetRelays,
  publishEventAndDisplay,
  publishEventOpaquely
} from '@/services/client.service'
import { ISigner, TAccount, TPublishOptions } from '@/types'
import { hexToBytes } from '@noble/hashes/utils.js'
import { now } from '@/lib/utils'
import { Event, VerifiedEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import * as nip19 from '@nostr/tools/nip19'
import * as nip49 from '@nostr/tools/nip49'
import i18n from 'i18next'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/error'
import { atom, getDefaultStore, useAtomValue } from 'jotai'
import { unwrap } from 'jotai/utils'
import { BunkerSigner } from '@/signers/bunker.signer'
import { Nip07Signer } from '@/signers/nip-07.signer'
import { NostrConnectionSigner } from '@/signers/nostrConnection.signer'
import { NpubSigner } from '@/signers/npub.signer'
import { NsecSigner } from '@/signers/nsec.signer'
import { loadRelayList } from '@nostr/gadgets/lists'
import { atomWithStorage } from 'jotai/utils'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { EventTemplate } from '@nostr/tools/core'
import { resetStore } from '@/services/store.service'
import { recreateOutbox } from '@/services/outbox.service'

const store = getDefaultStore()
const t = i18n.t.bind(i18n)

export const accountAtom = atomWithStorage<TAccount | null>('s:currentAccount', null, undefined, {
  getOnInit: true
})
export const loginDialogOpenAtom = atom<boolean>(false)
export const currentPubkeyAtom = atom<string | null>((get) => get(accountAtom)?.pubkey ?? null)

export function useCurrentPubkey(): string | null {
  return useAtomValue(currentPubkeyAtom)
}

export const canWriteAtom = atom((get) => {
  const signer = get(unwrappedSignerAtom)
  const account = get(accountAtom)
  return !!(signer && account?.signerType !== 'npub')
})

export async function preparePublishDraft(
  draftEvent: EventTemplate,
  options: TPublishOptions = {}
): Promise<{ event: VerifiedEvent; relayUrls: string[] }> {
  const profile = await store.get(currentProfileAtom)
  const account = store.get(accountAtom)
  const signer = await store.get(signerAtom)
  if (!account || !profile || !signer || account.signerType === 'npub') {
    throw new Error(t('You need to login first'))
  }

  const event = await signer.signEvent(draftEvent)

  if (event.kind !== kinds.Application && event.pubkey !== account.pubkey) {
    const eventAuthor = await loadNostrUser(event.pubkey)
    if (eventAuthor) addUsernameToIndex(eventAuthor)
    const result = confirm(
      t(
        'You are about to publish an event signed by [{{eventAuthorName}}]. You are currently logged in as [{{currentUsername}}]. Are you sure?',
        {
          eventAuthorName: eventAuthor.shortName,
          currentUsername: profile.shortName
        }
      )
    )
    if (!result) {
      throw new Error(t('Cancelled'))
    }
  }

  const relayUrls = await determineTargetRelays(event, options)
  return { event, relayUrls }
}

export async function publishDraft(
  draftEvent: EventTemplate,
  options: TPublishOptions = {}
): Promise<Event> {
  const { event, relayUrls } = await preparePublishDraft(draftEvent, options)
  try {
    await publishEventAndDisplay(relayUrls, event)
  } catch (error) {
    savePendingEvent(event)
    throw error
  }
  return event
}

// login / logout flow
async function login(signer: ISigner, act: TAccount, resetDb: boolean = false): Promise<string> {
  if (resetDb) {
    await resetStore()
    recreateOutbox()
  }

  if (act.signerType === 'npub') {
    console.debug('[encryption] npub: no encryption support')
    signer.nip44Decrypt = undefined
    signer.nip44Encrypt = undefined
  } else if (act.signerType === 'bunker') {
    console.debug('[encryption] bunker: probing nip04...')
    try {
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 5000)
      )
      const result = await Promise.race([signer.nip04Encrypt?.(act.pubkey, ''), timeout])
      console.debug('[encryption] bunker probe succeeded:', result)
    } catch (e) {
      console.debug('[encryption] bunker probe failed:', e)
      signer.nip44Decrypt = undefined
      signer.nip44Encrypt = undefined
    }
  } else {
    console.debug('[encryption] signerType', act.signerType, ': encryption supported')
  }

  store.set(accountAtom, act)
  ;(window as any).hallway.account = act.pubkey

  return act.pubkey
}

export function logout() {
  clearNsecRecord()
  store.set(accountAtom, null)
  store.set(pomegranateAuthAtom, null)
  ;(window as any).hallway.account = null
}

export async function nsecLogin(nsecOrHex: string, password?: string, needSetup?: boolean) {
  const nsecSigner = new NsecSigner()
  let privkey: Uint8Array
  if (nsecOrHex.startsWith('nsec')) {
    const { type, data } = nip19.decode(nsecOrHex)
    if (type !== 'nsec') throw new Error('invalid nsec or hex')
    privkey = data
  } else if (/^[0-9a-fA-F]{64}$/.test(nsecOrHex)) {
    privkey = hexToBytes(nsecOrHex)
  } else {
    throw new Error('invalid nsec or hex')
  }
  const pubkey = nsecSigner.login(privkey)!
  if (password) {
    const ncryptsec = nip49.encrypt(privkey, password)
    login(nsecSigner, { pubkey, signerType: 'ncryptsec', ncryptsec }, true)
  } else {
    login(nsecSigner, { pubkey, signerType: 'nsec', nsec: nip19.nsecEncode(privkey) }, true)
    recordNsecGenerated()
  }
  if (needSetup) {
    setupNewUser(nsecSigner)
  }
  return pubkey
}

export async function ncryptsecLogin(ncryptsec: string) {
  const password = prompt(t('Enter the password to decrypt your ncryptsec'))
  if (!password) throw new Error('Password is required')
  const privkey = nip49.decrypt(ncryptsec, password)
  const browserNsecSigner = new NsecSigner()
  const pubkey = browserNsecSigner.login(privkey)!
  return login(browserNsecSigner, { pubkey, signerType: 'ncryptsec', ncryptsec }, true)
}

export async function npubLogin(npub: string) {
  const npubSigner = new NpubSigner()
  const pubkey = npubSigner.login(npub)
  return login(npubSigner, { pubkey, signerType: 'npub', npub }, true)
}

export async function nip07Login() {
  try {
    const nip07Signer = new Nip07Signer()
    await nip07Signer.init()
    const pubkey = await nip07Signer.getPublicKey()
    if (!pubkey) throw new Error('You did not allow to access your pubkey')
    return login(nip07Signer, { pubkey, signerType: 'nip-07' }, true)
  } catch (err) {
    console.warn('login failed:', getErrorMessage(err))
    toast.error(t('Login failed') + ': ' + getErrorMessage(err))
    throw err
  }
}

export async function bunkerLogin(bunker: string) {
  const bunkerSigner = new BunkerSigner()
  const pubkey = await bunkerSigner.login(bunker)
  if (!pubkey) throw new Error('Invalid bunker')
  const bunkerUrl = new URL(bunker)
  bunkerUrl.searchParams.delete('secret')
  return login(
    bunkerSigner,
    {
      pubkey,
      signerType: 'bunker',
      bunker: bunkerUrl.toString(),
      bunkerClientSecretKey: bunkerSigner.getClientSecretKey()
    },
    true
  )
}

export async function nostrConnectionLogin(clientSecretKey: Uint8Array, connectionString: string) {
  const bunkerSigner = new NostrConnectionSigner(clientSecretKey, connectionString)
  const loginResult = await bunkerSigner.login()
  if (!loginResult.pubkey) throw new Error('Invalid bunker')
  const bunkerUrl = new URL(loginResult.bunkerString!)
  bunkerUrl.searchParams.delete('secret')
  return login(
    bunkerSigner,
    {
      pubkey: loginResult.pubkey,
      signerType: 'bunker',
      bunker: bunkerUrl.toString(),
      bunkerClientSecretKey: bunkerSigner.getClientSecretKey()
    },
    true
  )
}

export const signerAtom = atom<Promise<ISigner | null>>(async (get) => {
  const account = get(accountAtom)
  if (!account) return null

  if (account.signerType === 'nsec') {
    if (account.nsec) {
      const browserNsecSigner = new NsecSigner()
      browserNsecSigner.login(account.nsec)
      return browserNsecSigner
    }
  } else if (account.signerType === 'ncryptsec') {
    if (account.ncryptsec) {
      const password = prompt(t('Enter the password to decrypt your ncryptsec'))
      if (!password) return null
      const privkey = nip49.decrypt(account.ncryptsec, password)
      const browserNsecSigner = new NsecSigner()
      browserNsecSigner.login(privkey)
      return browserNsecSigner
    }
  } else if (account.signerType === 'nip-07') {
    const nip07Signer = new Nip07Signer()
    await nip07Signer.init()
    return nip07Signer
  } else if (account.signerType === 'bunker') {
    if (account.bunker && account.bunkerClientSecretKey) {
      const bunkerSigner = new BunkerSigner(account.bunkerClientSecretKey)
      await bunkerSigner.login(account.bunker, false)
      return bunkerSigner
    }
  } else if (account.signerType === 'npub' && account.npub) {
    const npubSigner = new NpubSigner()
    npubSigner.login(account.npub)
    return npubSigner
  }

  return null
})
export const unwrappedSignerAtom = unwrap(signerAtom)

async function setupNewUser(signer: ISigner) {
  const pubkey = await signer.getPublicKey()
  const { items: relays } = await loadRelayList(pubkey, window.hallway.universe.bigRelayUrls, true)
  const authorWriteRelays = relays.filter((r) => r.write).map((r) => r.url)
  const targetRelays = authorWriteRelays.length
    ? authorWriteRelays
    : window.hallway.universe.bigRelayUrls

  await Promise.allSettled([
    publishEventOpaquely(
      targetRelays,
      await signer.signEvent({
        kind: kinds.Contacts,
        content: '',
        created_at: now(),
        tags: []
      })
    ),
    publishEventOpaquely(
      targetRelays.concat(window.hallway.universe.indexerUrls),
      await signer.signEvent(
        createRelayListDraftEvent(
          window.hallway.universe.defaultInbox.map((url: string) => ({ url, scope: 'both' }))
        )
      )
    ),
    publishEventOpaquely(
      targetRelays,
      await signer.signEvent({
        kind: kinds.FavoriteRelays,
        content: '',
        tags: window.hallway.universe.defaultFavoriteRelays.map((url: string) => ['relay', url]),
        created_at: now()
      })
    ),
    publishEventOpaquely(
      targetRelays,
      await signer.signEvent({
        kind: kinds.BlossomServerList,
        content: '',
        tags: window.hallway.universe.defaultBlossomServers.map((url: string) => ['server', url]),
        created_at: now()
      })
    )
  ])
}
